import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../config";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { FileText, Zap, Repeat, Check, Loader2, ArrowRight } from "lucide-react";
import SEOHead from "../components/SEOHead";
import CompoundROIWidget from "../components/dashboard/CompoundROIWidget";

const PERKS_KEYS = [
  "bp_perk_1",
  "bp_perk_2",
  "bp_perk_3",
  "bp_perk_4",
  "bp_perk_5",
  "bp_perk_6",
];
const PERK_FALLBACKS = {
  bp_perk_1: "AI-generated SEO articles (500-800 words)",
  bp_perk_2: "2 contextual backlinks to your domain per article",
  bp_perk_3: "Unique target keyword + meta description",
  bp_perk_4: "Auto-published to TopRankPulse blog (indexed by Google)",
  bp_perk_5: "Translated into 26 languages automatically",
  bp_perk_6: "Configurable delivery rhythm — 1, 3 or 10 articles/day",
};

/**
 * Blog Packs pricing page — 3 one-time tiers + 1 subscription + 1 add-on.
 */
export default function BlogPacksPage() {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const perks = PERKS_KEYS.map((k) => t[k] || PERK_FALLBACKS[k]);

  useEffect(() => {
    axios
      .get(`${API}/blog-packs/pricing`)
      .then((r) => setPacks(r.data.packs || []))
      .finally(() => setLoading(false));
  }, []);

  const buy = async (pid) => {
    if (!user?.email) {
      navigate("/login?next=/blog-packs");
      return;
    }
    setBusy(pid);
    setErr(null);
    try {
      const r = await axios.post(`${API}/payments/checkout`, {
        package_id: pid,
        origin_url: window.location.origin,
        user_email: user.email,
        language: language || "en",
      });
      if (r.data?.url) window.location.href = r.data.url;
    } catch (e) {
      setErr(e.response?.data?.detail || "Checkout failed");
    } finally {
      setBusy(null);
    }
  };

  const oneTime = packs.filter((p) => p.id.startsWith("blog_pack_") && p.type === "one_time" && p.id !== "blog_pack_turbo");
  const subscription = packs.find((p) => p.id === "blog_pack_sub_100");
  const turbo = packs.find((p) => p.id === "blog_pack_turbo");

  return (
    <div
      className="min-h-screen themed-section"
      style={{ background: "var(--color-bg)", color: "var(--color-text)" }}
      data-testid="blog-packs-page"
    >
      <SEOHead
        slug="blog-packs"
        title="SEO Blog Packs — 25/50/100 AI Articles Auto-Published | TopRankPulse"
        description="Buy 25, 50 or 100 SEO-optimized AI blog articles. Auto-published to your domain with backlinks. Translated into 26 languages. From €25."
      />

      {/* Sticky top */}
      <div
        className="sticky top-0 z-40 backdrop-blur border-b"
        style={{
          background: "color-mix(in srgb, var(--color-bg) 80%, transparent)",
          borderColor: "var(--color-border)",
        }}
      >
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="font-black text-xl">
            <span style={{ color: "var(--color-primary)" }}>Top</span>RankPulse
          </Link>
          {user?.email ? (
            <Link
              to="/dashboard?tab=blog-packs"
              className="px-3 py-1.5 text-xs font-bold rounded-lg"
              style={{ background: "var(--color-primary)", color: "var(--color-bg)" }}
              data-testid="nav-my-packs"
            >
              {t.bp_my_packs || "My Packs"} →
            </Link>
          ) : (
            <Link
              to="/login"
              className="px-3 py-1.5 text-xs font-bold rounded-lg"
              style={{ background: "var(--color-primary)", color: "var(--color-bg)" }}
            >
              {t.nav_sign_in || "Sign in"}
            </Link>
          )}
        </div>
      </div>

      {/* HERO */}
      <section className="max-w-6xl mx-auto px-6 lg:px-12 pt-16 pb-8 text-center">
        <div
          className="inline-flex items-center gap-2 px-3 py-1 mb-4 rounded-full"
          style={{
            background: "color-mix(in srgb, var(--color-primary) 15%, transparent)",
            color: "var(--color-primary)",
          }}
        >
          <FileText className="w-3.5 h-3.5" />
          <span className="text-[10px] font-mono uppercase tracking-widest font-bold">
            {t.bp_eyebrow || "SEO Blog Packs"}
          </span>
        </div>
        <h1
          className="text-4xl sm:text-5xl lg:text-6xl font-black font-['Cabinet_Grotesk'] tracking-tighter leading-[0.95] mb-5"
          style={{ color: "var(--color-text)" }}
        >
          {t.bp_hero_title || "100 SEO articles."}{" "}
          <span
            className="bg-clip-text text-transparent"
            style={{
              backgroundImage:
                "linear-gradient(90deg, var(--color-primary) 0%, var(--color-accent) 100%)",
            }}
          >
            €50.
          </span>
        </h1>
        <p className="text-lg max-w-2xl mx-auto mb-8" style={{ color: "var(--color-text-muted)" }}>
          {t.bp_hero_sub || "Let PulseAI Engine™ write, optimize, and auto-publish SEO articles with backlinks to your site. You pick the rhythm — we handle the rest."}
        </p>
      </section>

      {err && (
        <div className="max-w-5xl mx-auto px-6 pb-2">
          <p className="text-sm text-center" style={{ color: "#ef4444" }}>{err}</p>
        </div>
      )}

      {/* Pricing grid */}
      <section className="max-w-6xl mx-auto px-6 lg:px-12 pb-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: "var(--color-primary)" }} />
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-4">
            {oneTime.map((p) => {
              const perArticle = (p.amount_eur / p.pack_size).toFixed(2);
              const isScale = p.id === "blog_pack_100";
              return (
                <div
                  key={p.id}
                  data-testid={`pack-card-${p.id}`}
                  className="relative rounded-2xl border p-6 flex flex-col"
                  style={{
                    background: "var(--color-surface)",
                    borderColor: isScale ? "var(--color-primary)" : "var(--color-border)",
                    borderWidth: isScale ? 2 : 1,
                  }}
                >
                  {isScale && (
                    <span
                      className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-[10px] font-mono uppercase tracking-widest font-bold rounded-full"
                      style={{ background: "var(--color-primary)", color: "var(--color-bg)" }}
                    >
                      {t.bp_best_value || "Best value"}
                    </span>
                  )}
                  <p className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: "var(--color-text-muted)" }}>
                    {p.name.replace(/\(.*$/, "").trim()}
                  </p>
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-5xl font-black" style={{ color: "var(--color-text)" }}>€{p.amount_eur}</span>
                    <span className="text-xs" style={{ color: "var(--color-text-muted)" }}>{t.bp_one_time || "one-time"}</span>
                  </div>
                  <p className="text-sm mb-5" style={{ color: "var(--color-text-muted)" }}>
                    <span className="font-bold" style={{ color: "var(--color-primary)" }}>{p.pack_size}</span> {t.bp_articles || "articles"} · €{perArticle}/{t.bp_per_article || "article"}
                  </p>
                  <ul className="space-y-2 mb-6 flex-1">
                    {perks.slice(0, 4).map((perk, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm" style={{ color: "var(--color-text)" }}>
                        <Check className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "var(--color-primary)" }} />
                        <span>{perk}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => buy(p.id)}
                    disabled={busy === p.id}
                    data-testid={`buy-${p.id}`}
                    className="w-full px-4 py-3 font-bold rounded-lg flex items-center justify-center gap-2 transition-transform hover:scale-[1.02]"
                    style={{
                      background: isScale ? "var(--color-primary)" : "var(--color-surface-alt)",
                      color: isScale ? "var(--color-bg)" : "var(--color-text)",
                      border: isScale ? "none" : "1px solid var(--color-border)",
                    }}
                  >
                    {busy === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                      <>{(t.bp_get || "Get")} {p.pack_size} {t.bp_articles || "articles"} <ArrowRight className="w-4 h-4" /></>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Subscription + Turbo row */}
        <div className="grid md:grid-cols-2 gap-4 mt-6">
          {subscription && (
            <div
              data-testid="pack-card-sub"
              className="rounded-2xl border p-6 flex items-center gap-4"
              style={{
                background: "linear-gradient(135deg, color-mix(in srgb, var(--color-primary) 12%, transparent), color-mix(in srgb, var(--color-accent) 12%, transparent))",
                borderColor: "var(--color-primary)",
              }}
            >
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                style={{ background: "var(--color-primary)" }}
              >
                <Repeat className="w-6 h-6" style={{ color: "var(--color-bg)" }} />
              </div>
              <div className="flex-1">
                <p className="font-black text-base" style={{ color: "var(--color-text)" }}>
                  {t.bp_sub_title_prefix || "Subscription"} — 100 {t.bp_articles || "articles"}/{t.bp_month || "month"} {t.bp_for || "for"} €{subscription.amount_eur}/mo
                </p>
                <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
                  {t.bp_sub_note || "Auto-refills monthly · cancel anytime · best long-term SEO compounding"}
                </p>
              </div>
              <button
                onClick={() => buy(subscription.id)}
                disabled={busy === subscription.id}
                data-testid="buy-blog_pack_sub_100"
                className="px-4 py-2.5 text-sm font-bold rounded-lg"
                style={{ background: "var(--color-primary)", color: "var(--color-bg)" }}
              >
                {busy === subscription.id ? <Loader2 className="w-4 h-4 animate-spin" /> : (t.bp_subscribe || "Subscribe →")}
              </button>
            </div>
          )}

          {turbo && (
            <div
              data-testid="pack-card-turbo"
              className="rounded-2xl border p-6 flex items-center gap-4"
              style={{ background: "var(--color-surface)", borderColor: "var(--color-border)" }}
            >
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                style={{ background: "var(--color-accent)" }}
              >
                <Zap className="w-6 h-6" style={{ color: "var(--color-bg)" }} />
              </div>
              <div className="flex-1">
                <p className="font-black text-base" style={{ color: "var(--color-text)" }}>
                  {t.bp_turbo_title || "Turbo Delivery add-on"} — €{turbo.amount_eur}
                </p>
                <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
                  {t.bp_turbo_note || "Upgrade your latest pack to 10 articles/day (vs 1-3). Purchase after a pack."}
                </p>
              </div>
              <button
                onClick={() => buy(turbo.id)}
                disabled={busy === turbo.id}
                data-testid="buy-blog_pack_turbo"
                className="px-4 py-2.5 text-sm font-bold rounded-lg border"
                style={{
                  borderColor: "var(--color-accent)",
                  color: "var(--color-accent)",
                  background: "transparent",
                }}
              >
                {busy === turbo.id ? <Loader2 className="w-4 h-4 animate-spin" /> : (t.bp_add_turbo || "Add Turbo")}
              </button>
            </div>
          )}
        </div>

        <ul className="mt-10 grid sm:grid-cols-2 gap-3 max-w-3xl mx-auto">
          {perks.map((perk, i) => (
            <li key={i} className="flex items-start gap-2 text-sm" style={{ color: "var(--color-text-muted)" }}>
              <Check className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "var(--color-primary)" }} />
              <span>{perk}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Compound ROI preview — simulates Subscription (100/mo) to show visitors long-term value */}
      <section className="max-w-5xl mx-auto px-6 lg:px-12 pb-16">
        <p
          className="text-center text-[10px] font-mono uppercase tracking-widest mb-3"
          style={{ color: "var(--color-accent)" }}
        >
          {t.bp_roi_eyebrow || "What €49/mo looks like in 12 months"}
        </p>
        <h2
          className="text-2xl sm:text-3xl font-black font-['Cabinet_Grotesk'] tracking-tighter text-center mb-6"
          style={{ color: "var(--color-text)" }}
        >
          {t.bp_roi_title || "The math of compounding SEO"}
        </h2>
        <CompoundROIWidget packs={[{ is_subscription: true, status: "active", pack_size: 100 }]} />
      </section>

      <footer className="py-10 text-center text-xs border-t" style={{ color: "var(--color-text-muted)", borderColor: "var(--color-border)" }}>
        <Link to="/" className="hover:opacity-80">← {t.bp_back || "Back to TopRankPulse"}</Link>
      </footer>
    </div>
  );
}
